import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import {
  format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear,
} from "date-fns";
import {
  Loader2, Download, CalendarIcon, FileSpreadsheet, Vault, ArrowUpFromLine,
  ArrowDownToLine, Save, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  getCashflowReport, saveCashflowNote, getSafeBalance, getSafeHistory,
  createSafeWithdrawal, deleteSafeWithdrawal,
} from "@/lib/api";

const RANGE_PRESETS = [
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "this_year", label: "This Year" },
  { value: "custom", label: "Custom Range" },
];

function getPresetDates(preset) {
  const today = new Date();
  switch (preset) {
    case "last_week": {
      const s = subDays(startOfWeek(today, { weekStartsOn: 1 }), 7);
      return { start: s, end: endOfWeek(s, { weekStartsOn: 1 }) };
    }
    case "this_month": return { start: startOfMonth(today), end: endOfMonth(today) };
    case "last_month": {
      const lm = subDays(startOfMonth(today), 1);
      return { start: startOfMonth(lm), end: endOfMonth(lm) };
    }
    case "this_year": return { start: startOfYear(today), end: endOfYear(today) };
    default: return { start: startOfWeek(today, { weekStartsOn: 1 }), end: endOfWeek(today, { weekStartsOn: 1 }) };
  }
}

const WITHDRAWAL_TYPES = [
  { value: "bank_deposit", label: "Bank Deposit" },
  { value: "cash_retention", label: "Cash Retention" },
  { value: "other", label: "Other" },
];

export default function CashFlowReport() {
  const { selectedBranch, branches } = useOutletContext();
  const branchId = selectedBranch;
  const branchName = branches.find((b) => b.id === branchId)?.name || "this branch";

  const [preset, setPreset] = useState("this_week");
  const [customStart, setCustomStart] = useState(() => getPresetDates("this_week").start);
  const [customEnd, setCustomEnd] = useState(() => getPresetDates("this_week").end);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState({ total_deposits: 0, total_withdrawals: 0, current_balance: 0 });
  const [history, setHistory] = useState([]);
  const [noteDraft, setNoteDraft] = useState({});
  const [savingNote, setSavingNote] = useState(null);

  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({
    date: new Date(), amount: "", withdrawal_type: "bank_deposit", notes: "",
  });

  const handlePresetChange = (value) => {
    setPreset(value);
    if (value !== "custom") {
      const { start, end } = getPresetDates(value);
      setCustomStart(start);
      setCustomEnd(end);
    }
  };

  const fetchAll = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    // Resilient: a failure in one stream (e.g. safe balance) must not blank the others.
    const [repR, balR, histR] = await Promise.allSettled([
      getCashflowReport(format(customStart, "yyyy-MM-dd"), format(customEnd, "yyyy-MM-dd"), branchId),
      getSafeBalance(branchId),
      getSafeHistory({ branch_id: branchId }),
    ]);
    const rep = repR.status === "fulfilled" ? repR.value : { rows: [], totals: {} };
    setReport(rep);
    setBalance(balR.status === "fulfilled" && balR.value
      ? balR.value
      : { total_deposits: 0, total_withdrawals: 0, current_balance: 0 });
    setHistory(histR.status === "fulfilled" && Array.isArray(histR.value) ? histR.value : []);
    const drafts = {};
    (rep.rows || []).forEach((r) => { drafts[r.date] = r.notes || ""; });
    setNoteDraft(drafts);
    if (repR.status === "rejected" && balR.status === "rejected") {
      toast.error("Failed to load cash flow data");
    }
    setLoading(false);
  }, [customStart, customEnd, branchId]);

  useEffect(() => {
    let active = true;
    Promise.resolve().then(() => { if (active) fetchAll(); });
    return () => { active = false; };
  }, [fetchAll]);

  const handleSaveNote = async (date) => {
    setSavingNote(date);
    try {
      await saveCashflowNote(date, branchId, noteDraft[date] || "");
      toast.success("Note saved");
      setReport((prev) => prev && {
        ...prev,
        rows: prev.rows.map((r) => (r.date === date ? { ...r, notes: noteDraft[date] || "" } : r)),
      });
    } catch {
      toast.error("Failed to save note");
    } finally {
      setSavingNote(null);
    }
  };

  const openWithdraw = (date) => {
    setWithdrawForm({ date: date ? new Date(date) : new Date(), amount: "", withdrawal_type: "bank_deposit", notes: "" });
    setWithdrawOpen(true);
  };

  const handleWithdraw = async () => {
    if (!withdrawForm.amount || parseFloat(withdrawForm.amount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setSubmitting(true);
    try {
      const amt = parseFloat(withdrawForm.amount);
      if (balance && amt > balance.current_balance) {
        toast.warning("Heads up: this withdrawal exceeds the current safe balance");
      }
      await createSafeWithdrawal({
        date: format(withdrawForm.date, "yyyy-MM-dd"),
        branch_id: branchId,
        amount: parseFloat(withdrawForm.amount),
        withdrawal_type: withdrawForm.withdrawal_type,
        notes: withdrawForm.notes,
      });
      toast.success("Withdrawal recorded");
      setWithdrawOpen(false);
      fetchAll();
    } catch {
      toast.error("Failed to record withdrawal");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteWithdrawal = async (id) => {
    try {
      await deleteSafeWithdrawal(id);
      toast.success("Withdrawal deleted");
      fetchAll();
    } catch {
      toast.error("Failed to delete withdrawal");
    }
  };

  const exportCSV = () => {
    if (!report?.rows?.length) return;
    const headers = ["Date", "Opening Cash", "Cash Revenue", "Eftpos Revenue", "Other Income", "Daily Expenses", "Safe Deposit", "Daily Staff Cash Payout", "Closing Cash", "Note"];
    const csvRows = [headers.join(",")];
    report.rows.forEach((r) => {
      csvRows.push([r.date, (r.opening_cash ?? 0).toFixed(2), (r.cash_revenue ?? 0).toFixed(2), (r.eftpos_revenue ?? 0).toFixed(2),
        (r.other_income ?? 0).toFixed(2), (r.daily_expenses ?? 0).toFixed(2), (r.safe_deposit ?? 0).toFixed(2),
        (r.daily_staff_payout ?? 0).toFixed(2), (r.closing_cash ?? 0).toFixed(2),
        `"${(r.notes || "").replace(/"/g, '""')}"`].join(","));
    });
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cashflow_${branchId}_${format(new Date(), "yyyyMMdd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 animate-in">
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h2 className="font-heading text-2xl font-bold text-forest flex items-center gap-2">
            <FileSpreadsheet className="h-6 w-6" /> Cash Flow &amp; Safe
          </h2>
          <p className="text-sage text-sm">Daily reconciliation and safe tracking — {branchName}</p>
        </div>
        <div className="flex items-center gap-2">
          {report?.rows?.length > 0 && (
            <Button variant="outline" onClick={exportCSV} data-testid="export-csv-btn">
              <Download className="h-4 w-4 mr-2" /> Export
            </Button>
          )}
        </div>
      </div>

      {/* Safe balance cards */}
      {balance && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-2 border-forest/20">
            <CardContent className="pt-6 flex items-center gap-3">
              <div className="p-3 bg-forest/10 rounded-xl"><Vault className="h-6 w-6 text-forest" /></div>
              <div><p className="text-sm text-sage">Safe Balance</p>
                <p className={`text-3xl font-heading font-bold ${(balance.current_balance ?? 0) < 0 ? "text-destructive" : "text-forest"}`} data-testid="safe-balance">${(balance.current_balance ?? 0).toFixed(2)}</p></div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="pt-6 flex items-center gap-3">
              <div className="p-3 bg-sage/10 rounded-xl"><ArrowDownToLine className="h-6 w-6 text-sage" /></div>
              <div><p className="text-sm text-sage">Total Deposits</p>
                <p className="text-2xl font-heading font-bold text-forest" data-testid="total-deposits">${(balance.total_deposits ?? 0).toFixed(2)}</p></div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="pt-6 flex items-center gap-3">
              <div className="p-3 bg-terracotta/10 rounded-xl"><ArrowUpFromLine className="h-6 w-6 text-terracotta" /></div>
              <div><p className="text-sm text-sage">Total Withdrawals</p>
                <p className="text-2xl font-heading font-bold text-terracotta" data-testid="total-withdrawals">${(balance.total_withdrawals ?? 0).toFixed(2)}</p></div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end bg-white border border-border rounded-xl p-4">
        <div className="space-y-1">
          <label className="text-xs font-medium text-sage">Date Range</label>
          <Select value={preset} onValueChange={handlePresetChange}>
            <SelectTrigger className="w-[160px]" data-testid="range-preset-select"><SelectValue /></SelectTrigger>
            <SelectContent>{RANGE_PRESETS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-sage">Start</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[150px]" data-testid="custom-start-date">
                <CalendarIcon className="mr-2 h-3 w-3" />{format(customStart, "MMM d, yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar mode="single" selected={customStart} onSelect={(d) => { if (d) { setCustomStart(d); setPreset("custom"); } }} />
            </PopoverContent>
          </Popover>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-sage">End</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[150px]" data-testid="custom-end-date">
                <CalendarIcon className="mr-2 h-3 w-3" />{format(customEnd, "MMM d, yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar mode="single" selected={customEnd} onSelect={(d) => { if (d) { setCustomEnd(d); setPreset("custom"); } }} />
            </PopoverContent>
          </Popover>
        </div>
        <Button onClick={fetchAll} disabled={loading} className="bg-forest hover:bg-forest-light" data-testid="generate-report-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Refresh
        </Button>
      </div>

      {/* Report table — new 11-column ledger */}
      {report && (() => { const showPayoutCol = report.daily_payout_enabled !== false; return (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          {report.rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-sage"><p>No data for this period</p></div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="cashflow-report-table">
                <TableHeader>
                  <TableRow className="bg-sand">
                    <TableHead className="table-header-cell">Date</TableHead>
                    <TableHead className="table-header-cell text-right">Opening Cash</TableHead>
                    <TableHead className="table-header-cell text-right">Cash Revenue</TableHead>
                    <TableHead className="table-header-cell text-right">Eftpos Revenue</TableHead>
                    <TableHead className="table-header-cell text-right">Other Income</TableHead>
                    <TableHead className="table-header-cell text-right">Daily Expenses</TableHead>
                    <TableHead className="table-header-cell text-right">Safe Deposit</TableHead>
                    {showPayoutCol && <TableHead className="table-header-cell text-right">Daily Staff Cash Payout</TableHead>}
                    <TableHead className="table-header-cell text-right">Closing Cash</TableHead>
                    <TableHead className="table-header-cell min-w-[180px]">Note</TableHead>
                    <TableHead className="table-header-cell w-24">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.rows.map((row, idx) => (
                    <TableRow key={row.date} className={idx % 2 ? "bg-sand/20" : ""} data-testid={`cashflow-row-${row.date}`}>
                      <TableCell className="font-medium text-sm">{row.date}</TableCell>
                      <TableCell className="text-right text-sm">${(row.opening_cash ?? 0).toFixed(2)}</TableCell>
                      <TableCell className="text-right text-sm text-forest">${(row.cash_revenue ?? 0).toFixed(2)}</TableCell>
                      <TableCell className="text-right text-sm text-terracotta">${(row.eftpos_revenue ?? 0).toFixed(2)}</TableCell>
                      <TableCell className="text-right text-sm text-forest">${(row.other_income ?? 0).toFixed(2)}</TableCell>
                      <TableCell className="text-right text-sm text-destructive">${(row.daily_expenses ?? 0).toFixed(2)}</TableCell>
                      <TableCell className="text-right text-sm text-ochre">${(row.safe_deposit ?? 0).toFixed(2)}</TableCell>
                      {showPayoutCol && <TableCell className="text-right text-sm text-destructive">${(row.daily_staff_payout ?? 0).toFixed(2)}</TableCell>}
                      <TableCell className="text-right text-sm font-semibold text-forest">${(row.closing_cash ?? 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Input
                            value={noteDraft[row.date] ?? ""}
                            onChange={(e) => setNoteDraft((p) => ({ ...p, [row.date]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === "Enter") handleSaveNote(row.date); }}
                            placeholder="Add note…"
                            className="h-8 text-sm"
                            data-testid={`note-input-${row.date}`}
                          />
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-forest shrink-0"
                            onClick={() => handleSaveNote(row.date)} disabled={savingNote === row.date}
                            title="Save note" data-testid={`save-note-${row.date}`}>
                            {savingNote === row.date ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="outline" className="h-8 text-xs border-ochre text-ochre hover:bg-ochre hover:text-white"
                          onClick={() => openWithdraw(row.date)} data-testid={`row-withdraw-${row.date}`}>
                          <ArrowUpFromLine className="h-3 w-3 mr-1" /> Withdraw
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-forest/5 border-t-2 border-forest/20 font-semibold">
                    <TableCell className="text-right font-bold text-forest">TOTALS</TableCell>
                    <TableCell />
                    <TableCell className="text-right text-forest">${(report.totals.cash_revenue ?? 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-terracotta">${(report.totals.eftpos_revenue ?? 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-forest">${(report.totals.other_income ?? 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-destructive">${(report.totals.daily_expenses ?? 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-ochre">${(report.totals.safe_deposit ?? 0).toFixed(2)}</TableCell>
                    {showPayoutCol && <TableCell className="text-right text-destructive">${(report.totals.daily_staff_payout ?? 0).toFixed(2)}</TableCell>}
                    <TableCell colSpan={3} />
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      ); })()}

      {/* Safe history */}
      <Card className="border-border">
        <CardHeader className="pb-3"><CardTitle className="font-heading text-forest text-lg">Safe History</CardTitle></CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sage text-sm">No safe transactions yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="safe-history-table">
                <TableHeader>
                  <TableRow className="bg-sand">
                    <TableHead className="table-header-cell">Date</TableHead>
                    <TableHead className="table-header-cell">Type</TableHead>
                    <TableHead className="table-header-cell">Description</TableHead>
                    <TableHead className="table-header-cell text-right">Amount</TableHead>
                    <TableHead className="table-header-cell w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((item) => (
                    <TableRow key={`${item.type}-${item.id}`} data-testid={`safe-row-${item.id}`}>
                      <TableCell className="text-sm font-medium">{item.date}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={item.type === "deposit" ? "bg-forest/10 text-forest border-forest/30" : "bg-terracotta/10 text-terracotta border-terracotta/30"}>
                          {item.type === "deposit" ? "IN" : "OUT"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-sage">{item.description}</TableCell>
                      <TableCell className={`text-right font-medium ${item.type === "deposit" ? "text-forest" : "text-terracotta"}`}>
                        {item.type === "deposit" ? "+" : "-"}${item.amount.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {item.type === "withdrawal" && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                            onClick={() => handleDeleteWithdrawal(item.id)} data-testid={`delete-withdrawal-${item.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Withdraw dialog */}
      <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
        <DialogContent className="sm:max-w-md" data-testid="withdraw-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-forest">Withdraw from Safe</DialogTitle>
            <DialogDescription>Record money taken out of the safe at {branchName}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm text-forest">Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full" data-testid="withdraw-date-btn">
                    <CalendarIcon className="mr-2 h-3 w-3" />{format(withdrawForm.date, "MMM d, yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={withdrawForm.date} onSelect={(d) => d && setWithdrawForm((p) => ({ ...p, date: d }))} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-forest">Amount ($) *</Label>
              <Input type="number" step="0.01" min="0" value={withdrawForm.amount}
                onChange={(e) => setWithdrawForm((p) => ({ ...p, amount: e.target.value }))}
                placeholder="0.00" data-testid="withdraw-amount-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-forest">Type</Label>
              <Select value={withdrawForm.withdrawal_type} onValueChange={(v) => setWithdrawForm((p) => ({ ...p, withdrawal_type: v }))}>
                <SelectTrigger data-testid="withdraw-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{WITHDRAWAL_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-forest">Notes</Label>
              <Textarea value={withdrawForm.notes} onChange={(e) => setWithdrawForm((p) => ({ ...p, notes: e.target.value }))}
                placeholder="Reason…" rows={2} data-testid="withdraw-notes-input" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setWithdrawOpen(false)}>Cancel</Button>
            <Button onClick={handleWithdraw} disabled={submitting} className="bg-forest hover:bg-forest-light" data-testid="confirm-withdraw-btn">
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Record Withdrawal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
